/** Camera target for the inventory map given the currently plotted buildings. */

export type MapCamera =
  | { type: "none" }
  | { type: "overview" }
  | { type: "center"; center: [number, number]; zoom: number }
  | { type: "bounds"; bounds: [[number, number], [number, number]] };

const SINGLE_BUILDING_ZOOM = 12;

/** Finland-framed default when the scope is the whole kanta (or a scope with nothing to plot). */
export const PORTFOLIO_OVERVIEW_VIEW = {
  longitude: 25.0,
  latitude: 64.5,
  zoom: 4.5,
} as const;

export type CameraFitMode = "overview" | "leave";

/** Stable key so the camera re-applies when ScopeBar changes even if plotted coords don't. */
export function inventoryScopeKey(input: {
  buildingId: string | null;
  groupId: string | null;
  city: string;
  type: string;
  status: string;
}): string {
  return `${input.buildingId ?? ""}:${input.groupId ?? ""}:${input.city}:${input.type}:${input.status}`;
}

/** Camera targets: a selected building if it is on the map, otherwise the whole plotted set. */
export function fitPointsForScope<T extends { id: string }>(
  points: readonly T[],
  buildingId: string | null,
): T[] {
  if (!buildingId) return [...points];
  return points.filter((p) => p.id === buildingId);
}

/**
 * Fits the map to the buildings the current inventory scope shows.
 * `emptyMode` is `overview` for kanta/salkku (zoom back out to Finland) and `leave`
 * when a single building is selected but has no coordinates — never a city centroid (ADR-018).
 */
export function cameraForPoints(
  points: readonly { lat: number; lon: number }[],
  emptyMode: CameraFitMode = "leave",
): MapCamera {
  if (points.length === 0) {
    return emptyMode === "overview" ? { type: "overview" } : { type: "none" };
  }
  if (points.length === 1) {
    return {
      type: "center",
      center: [points[0].lon, points[0].lat],
      zoom: SINGLE_BUILDING_ZOOM,
    };
  }

  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    west = Math.min(west, p.lon);
    east = Math.max(east, p.lon);
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
  }
  // A collapsed box (two buildings on the same point) makes MapLibre fitBounds a no-op.
  if (west === east) {
    west -= 0.01;
    east += 0.01;
  }
  if (south === north) {
    south -= 0.01;
    north += 0.01;
  }
  return {
    type: "bounds",
    bounds: [
      [west, south],
      [east, north],
    ],
  };
}
